import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useAuth, usePermission } from '@saas-kit/lib';
import { Card, CardContent, EmptyState, ErrorBanner, PageHeader } from '@saas-kit/ui';
import {
  DataTable,
  type DataTableColumn,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { type InvoiceRecord, listInvoices } from './api';
import { InvoiceDetailDrawer } from './InvoiceDetailDrawer';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';

const DEFAULT_PAGE_SIZE = 25;

export function BillingPage(): JSX.Element {
  const { t } = useTranslation('billing');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canRead = usePermission('billing.read');
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [active, setActive] = useState<InvoiceRecord | null>(null);

  const invoicesQuery = useQuery({
    queryKey: [
      'tenant-billing-invoices',
      selectedTenant?.tenant_id,
      { offset, limit: pageSize },
    ],
    queryFn: () => listInvoices({ offset, limit: pageSize }),
    enabled: Boolean(selectedTenant?.tenant_id && canRead),
    placeholderData: (prev) => prev,
  });

  const invoices = invoicesQuery.data?.items ?? [];
  const page = invoicesQuery.data?.page;
  const total = page?.total ?? 0;

  const columns: DataTableColumn<InvoiceRecord>[] = useMemo(
    () => [
      {
        id: 'invoice_number',
        header: t('table.invoice_number'),
        cell: (row) => (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => setActive(row)}
          >
            {row.invoice_number}
          </button>
        ),
      },
      {
        id: 'status',
        header: t('table.status'),
        cell: (row) => <InvoiceStatusBadge status={row.status} />,
        exportValue: (row) => row.status,
      },
      {
        id: 'total',
        header: t('table.total'),
        align: 'end',
        cell: (row) => `${row.total_amount} ${row.currency}`,
        exportValue: (row) => `${row.total_amount} ${row.currency}`,
      },
      {
        id: 'amount_paid',
        header: t('table.paid'),
        align: 'end',
        cell: (row) => `${row.amount_paid} ${row.currency}`,
        exportValue: (row) => `${row.amount_paid} ${row.currency}`,
      },
      {
        id: 'due_at',
        header: t('table.due_at'),
        cell: (row) =>
          row.due_at ? new Date(row.due_at).toLocaleDateString() : '—',
        exportValue: (row) => row.due_at ?? '',
        hideOnMobile: true,
      },
      {
        id: 'issued_at',
        header: t('table.issued_at'),
        cell: (row) =>
          row.issued_at ? new Date(row.issued_at).toLocaleDateString() : '—',
        exportValue: (row) => row.issued_at ?? '',
        hideOnMobile: true,
      },
    ],
    [t],
  );

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext: page ? page.offset + page.limit < page.total : false,
    hasPrev: offset > 0,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty =
    Boolean(selectedTenant) &&
    canRead &&
    !invoicesQuery.isLoading &&
    !invoicesQuery.isError &&
    total === 0;

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('subtitle')} />

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : !canRead ? (
        <ErrorBanner>{t('errors.denied')}</ErrorBanner>
      ) : showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              title={t('empty.title')}
              description={t('empty.body')}
            />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          data={invoices}
          columns={columns}
          rowKey={(row) => row.id}
          testId="tenant-billing-invoices"
          selectable
          pagination={pagination}
          isLoading={invoicesQuery.isLoading}
          error={invoicesQuery.isError ? t('errors.load_failed') : null}
          exportable
        />
      )}

      {selectedTenant && active ? (
        <InvoiceDetailDrawer
          invoice={active}
          open={Boolean(active)}
          onOpenChange={(o) => !o && setActive(null)}
        />
      ) : null}
    </div>
  );
}
